import { useCallback, useEffect, useState } from "react";
import { Laptop, LogOut, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
const isPhone = (device) => /Android|iPhone|iPad/.test(device || "");

/** Account menu → Signed-in devices: where this person is signed in; sign one out, or every other device. */
export default function DevicesDialog({ onClose }) {
  const { signOutOtherDevices } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/auth/sessions", { skipCache: true }).then(({ data }) => setRows(data)).catch(() => setRows("error"));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const endOne = async (row) => {
    setBusy(row.id);
    try {
      await api.delete(`/auth/sessions/${row.id}`);
      toast.success(`${row.device} signed out`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't sign that device out");
    } finally { setBusy(""); }
  };
  const endOthers = async () => {
    setBusy("others");
    try {
      const n = await signOutOtherDevices();
      toast.success(n ? `Signed out of ${n} other device${n === 1 ? "" : "s"}` : "Other devices signed out");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't sign the other devices out");
    } finally { setBusy(""); }
  };

  const list = Array.isArray(rows) ? rows : [];
  const others = list.filter((r) => !r.current).length;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="devices-title" onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg max-h-[85vh] flex flex-col bg-[var(--surface)] rounded-xl border border-[var(--border)]" data-testid="devices-dialog">
        <div className="flex items-center justify-between px-5 pt-5">
          <h2 id="devices-title" className="font-heading text-lg font-semibold">Signed-in devices</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-md hover:bg-[var(--surface-hover)]"><X size={16} /></button>
        </div>
        <p className="px-5 text-xs text-[var(--ink-3)] mt-1">Lost a phone, or used someone else's computer? Sign that device out here.</p>
        <ul className="mt-4 px-5 overflow-y-auto divide-y divide-[var(--border)]" aria-busy={rows === null}>
          {rows === null && <li className="py-6 text-sm text-[var(--ink-3)]">Loading…</li>}
          {rows === "error" && (
            <li className="py-6 text-sm text-[var(--danger)]" role="alert" data-testid="devices-error">
              Couldn't load your signed-in devices. Try again in a moment.
            </li>
          )}
          {Array.isArray(rows) && rows.length === 0 && <li className="py-6 text-sm text-[var(--ink-3)]">No sign-ins listed yet. Sign-ins from before this list existed don't show; "Sign out of all other devices" ends them too.</li>}
          {list.map((r) => (
            <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`device-${r.id}`}>
              <span className="w-9 h-9 rounded-lg bg-[var(--surface-2)] grid place-items-center text-[var(--ink-2)] shrink-0">
                {isPhone(r.device) ? <Smartphone size={17} /> : <Laptop size={17} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[var(--ink)] flex flex-wrap items-center gap-1.5">
                  {r.device}
                  {r.current && <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--moss)]">This device</span>}
                  {r.shared && <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--warn)]" title={`Signed out after ${r.idle_hours} h without use`}>Shared computer</span>}
                </div>
                <div className="text-xs text-[var(--ink-3)]">
                  {r.method} · signed in {when(r.created_at)} · last active {when(r.last_seen)}{r.ip ? ` · ${r.ip}` : ""}
                </div>
              </div>
              {!r.current && (
                <button type="button" onClick={() => endOne(r)} disabled={busy === r.id}
                        className="text-xs px-3 min-h-[36px] rounded-lg border border-[var(--border)] hover:border-[var(--danger)] hover:text-[var(--danger)] disabled:opacity-50"
                        data-testid={`device-signout-${r.id}`}>
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="px-5 py-4 mt-2 border-t border-[var(--border)]">
          <button type="button" onClick={endOthers} disabled={busy === "others"}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[var(--danger)]/40 text-[var(--danger)] text-sm font-semibold hover:bg-[var(--danger-soft)] disabled:opacity-50"
                  data-testid="devices-signout-others">
            <LogOut size={15} /> Sign out of all other devices{others ? ` (${others})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
